import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Nomenclator {
  id: number;
  code: string;
  category: string;
  name: string;
  description: string | null;
  display_order: number;
  color: string | null;
  icon: string | null;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface NomenclatorFilterOption {
  value: string;
  label: string;
  category?: string;
  code?: string;
  color?: string;
}
interface NomenclatorsState extends FilteredState<Nomenclator> {
  selectedNomenclator: Nomenclator | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  byCategory: Record<string, Nomenclator[]>;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: NomenclatorFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: NomenclatorsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'category',
  sortDirection: 'asc',
  searchTerm: null,
  selectedNomenclator: null,
  creating: false,
  updating: false,
  deleting: false,
  byCategory: {},
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
export const fetchNomenclators = createAsyncThunk<{ data: Nomenclator[]; totalCount: number }, any>(
  'nomenclators/fetchNomenclators',
  async (params: any) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchNomenclatorsByCategory = createAsyncThunk<Nomenclator[], string>(
  'nomenclators/fetchByCategory',
  async (category: string) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchNomenclatorById = createAsyncThunk('nomenclators/fetchNomenclatorById', async (id: number) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, categoría) - CACHEADO
export const fetchNomenclatorFilterOptions = createAsyncThunk(
  'nomenclators/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const nomenclatorsState = state.nomenclators as NomenclatorsState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      nomenclatorsState.filterOptionsLastFetch &&
      Date.now() - nomenclatorsState.filterOptionsLastFetch < CACHE_DURATION &&
      nomenclatorsState.filterOptions.length > 0
    ) {
      return nomenclatorsState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/nomenclators?startRow=0&endRow=1000&sortProperty=category,name&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const nomenclators = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: NomenclatorFilterOption[] = nomenclators.map((nomenclator: any) => ({
        value: nomenclator.id.toString(),
        label: `${nomenclator.category} - ${nomenclator.name}`,
        category: nomenclator.category,
        code: nomenclator.code,
        color: nomenclator.color
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const createNomenclator = createAsyncThunk('nomenclators/createNomenclator', async (nomenclator: Partial<Nomenclator>) => {
  throw new Error('Not implemented yet');
});
export const updateNomenclator = createAsyncThunk('nomenclators/updateNomenclator', async ({ id, data }: { id: number; data: Partial<Nomenclator> }) => {
  throw new Error('Not implemented yet');
});
export const deleteNomenclator = createAsyncThunk('nomenclators/deleteNomenclator', async (id: number) => {
  throw new Error('Not implemented yet');
});
const nomenclatorsSlice = createSlice({
  name: 'nomenclators',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectNomenclator: (state, action: PayloadAction<Nomenclator | null>) => {
      state.selectedNomenclator = action.payload;
    },
    clearSelection: (state) => {
      state.selectedNomenclator = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNomenclators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNomenclators.fulfilled, (state, action: PayloadAction<{ data: Nomenclator[]; totalCount: number }>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
        // Agrupar por categoría
        const byCategory: Record<string, Nomenclator[]> = {};
        action.payload.data.forEach((item: Nomenclator) => {
          if (!byCategory[item.category]) {
            byCategory[item.category] = [];
          }
          byCategory[item.category].push(item);
        });
        state.byCategory = byCategory;
      })
      .addCase(fetchNomenclators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar nomencladores';
      });
    builder
      .addCase(fetchNomenclatorsByCategory.fulfilled, (state, action: PayloadAction<Nomenclator[], string, { arg: string }>) => {
        state.byCategory[action.meta.arg] = action.payload;
      })
      // Casos para fetchNomenclatorFilterOptions (opciones cacheadas)
      .addCase(fetchNomenclatorFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchNomenclatorFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchNomenclatorFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectNomenclatorFilterOptions = (state: any) => state.nomenclators.filterOptions;
export const selectNomenclatorFilterOptionsLoading = (state: any) => state.nomenclators.filterOptionsLoading;
export const selectNomenclatorFilterOptionsError = (state: any) => state.nomenclators.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areNomenclatorFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.nomenclators.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectNomenclator, clearSelection } = nomenclatorsSlice.actions;
export default nomenclatorsSlice.reducer;
